import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';

const TemplateSelection = ({
  templates = [],
  selectedRace,
  selectedTemplate,
  selectedTemperament,
  onTemplateSelect
}) => {
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [localSelectedTemplate, setLocalSelectedTemplate] = useState(selectedTemplate);
  const [localSelectedTemperament, setLocalSelectedTemperament] = useState(selectedTemperament);

  // Фильтрация шаблонов по совместимости с расой
  useEffect(() => {
    if (!selectedRace) {
      setFilteredTemplates(templates);
      return;
    }

    const compatibleTemplates = templates.filter(template => {
      // Если у шаблона нет ограничений по расам, он совместим со всеми
      if (!template.race_compatibility || template.race_compatibility.length === 0) {
        return true;
      }
      // Проверяем совместимость
      return template.race_compatibility.includes(selectedRace.id);
    });

    setFilteredTemplates(compatibleTemplates);
  }, [templates, selectedRace]);

  // Обработчик выбора шаблона
  const handleTemplateSelect = (template) => {
    setLocalSelectedTemplate(template);
    // Устанавливаем темперамент по умолчанию из шаблона
    const defaultTemperament = template.temperament || 'adaptive';
    setLocalSelectedTemperament(defaultTemperament);
    
    // Сообщаем родительскому компоненту
    onTemplateSelect(template, defaultTemperament);
  };

  // Обработчик изменения темперамента
  const handleTemperamentChange = (event) => {
    const newTemperament = event.target.value;
    setLocalSelectedTemperament(newTemperament);
    
    // Обновляем в родительском компоненте
    if (localSelectedTemplate) {
      onTemplateSelect(localSelectedTemplate, newTemperament);
    }
  };

  // Доступные темпераменты
  const availableTemperaments = [
    { value: 'adaptive', label: 'Адаптивный', description: 'Легко приспосабливается к обстоятельствам' },
    { value: 'aggressive', label: 'Агрессивный', description: 'Решителен и напорист' },
    { value: 'cautious', label: 'Осторожный', description: 'Взвешивает риски перед действиями' },
    { value: 'creative', label: 'Творческий', description: 'Ищет нестандартные решения' },
    { value: 'logical', label: 'Логичный', description: 'Действует на основе разума и анализа' },
    { value: 'empathic', label: 'Эмпатичный', description: 'Чувствителен к эмоциям других, ориентирован на отношения' },
    { value: 'resilient', label: 'Устойчивый', description: 'Сохраняет стабильность в стрессе, быстро восстанавливается' },
    { value: 'exploratory', label: 'Исследовательский', description: 'Стремится к новому опыту, любопытен, авантюрен' }
  ];

  if (!selectedRace) {
    return (
      <Alert severity="warning" sx={{ my: 2 }}>
        Сначала выберите расу на предыдущем шаге
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Выбор профессии и характера
      </Typography>
      
      <Typography variant="body1" paragraph color="text.secondary">
        Выберите шаблон персонажа и определите его основной темперамент.
        {selectedRace && ` Доступные шаблоны для расы "${selectedRace.name}":`}
      </Typography>

      {/* Список доступных шаблонов */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {filteredTemplates.length === 0 ? (
          <Grid item xs={12}>
            <Alert severity="info">
              Нет доступных шаблонов для выбранной расы
            </Alert>
          </Grid>
        ) : (
          filteredTemplates.map((template) => (
            <Grid item xs={12} sm={6} md={4} key={template.id}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  border: localSelectedTemplate?.id === template.id ? 2 : 1,
                  borderColor: localSelectedTemplate?.id === template.id ? 'primary.main' : 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handleTemplateSelect(template)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="h6" component="div">
                      {template.icon || '👤'} {template.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {template.description || 'Описание отсутствует'}
                  </Typography>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  {/* Основные цели */}
                  {template.goals && template.goals.length > 0 && (
                    <Box mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Цели:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {template.goals.slice(0, 3).map((goal, index) => (
                          <Chip
                            key={index}
                            label={goal}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  {/* Основные навыки */}
                  {template.primary_skills && template.primary_skills.length > 0 && (
                    <Box mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Навыки:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {template.primary_skills.slice(0, 3).map((skill, index) => (
                          <Chip
                            key={index}
                            label={skill}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  {/* Темперамент по умолчанию */}
                  <Typography variant="caption" color="text.secondary">
                    Темперамент по умолчанию: <strong>{template.temperament || 'adaptive'}</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Выбор темперамента (только если выбран шаблон) */}
      {localSelectedTemplate && (
        <Box mt={4} p={3} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Настройка темперамента для {localSelectedTemplate.name}
          </Typography>
          
          <Typography variant="body2" paragraph color="text.secondary">
            Выберите основной темперамент персонажа. Темперамент влияет на поведение и принятие решений.
          </Typography>

          <FormControl component="fieldset">
            <FormLabel component="legend">Темперамент персонажа</FormLabel>
            <RadioGroup
              value={localSelectedTemperament}
              onChange={handleTemperamentChange}
            >
              <Grid container spacing={2}>
                {availableTemperaments.map((temp) => (
                  <Grid item xs={12} sm={6} key={temp.value}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        border: localSelectedTemperament === temp.value ? 2 : 1,
                        borderColor: localSelectedTemperament === temp.value ? 'primary.main' : 'divider'
                      }}
                    >
                      <FormControlLabel
                        value={temp.value}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="subtitle2">
                              {temp.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {temp.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
          </FormControl>

          {/* Информация о выборе */}
          {localSelectedTemplate && localSelectedTemperament && (
            <Box mt={3} p={2} sx={{ bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Выбрано:</strong> {localSelectedTemplate.name} с темпераментом "
                {availableTemperaments.find(t => t.value === localSelectedTemperament)?.label || localSelectedTemperament}"
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Предупреждение о несовместимости */}
      {selectedRace && filteredTemplates.length < templates.length && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Показано {filteredTemplates.length} из {templates.length} шаблонов, совместимых с расой "{selectedRace.name}"
        </Alert>
      )}
    </Box>
  );
};

export default TemplateSelection;